import type { Feature } from 'geojson'

import { IncidentsByMarketFacilityQuery } from '@/graphql/generated/operations'
import { GlobalMapProperties } from '@/utils/helpers'

import { ClusterPointIProps, PointType } from '../types/types'

// supercluster calls `map` on raw points only. `reduce` also receives child
// clusters, whose props keep the `type` of the point that seeded them, so the
// counts must be summed unconditionally rather than gated on `type`.
export const clusterOptions = {
  map: (props: ClusterPointIProps) => ({
    type: props.type,
    incidentCount: props.incidentCount ?? 0,
    facilityCount: props.type === PointType.Facility ? 1 : 0,
  }),
  reduce: (acc: ClusterPointIProps, props: ClusterPointIProps) => {
    acc.facilityCount += props.facilityCount ?? 0
    acc.incidentCount += props.incidentCount ?? 0
    return acc
  },
}

export function facilityToGeoJSONPoint(data: GlobalMapProperties[]): Feature[] {
  return (
    data?.map((facility) => {
      const feature: Feature = {
        type: 'Feature',
        id: facility.id,
        geometry: {
          type: 'Point',
          coordinates: [facility.lon, facility.lat],
        },
        properties: {
          id: facility.id,
          type: PointType.Facility,
          latitude: facility.lat,
          longitude: facility.lon,
          incidentCount: facility.activeIncidentCount,
          shortName: facility.shortName ?? `N/A`,
          floorId: facility.defaultFloorId,
        },
      }
      return feature
    }) || []
  )
}

export function marketIncidentsToGeoJSONPoint(
  data: IncidentsByMarketFacilityQuery
): Feature[] {
  return (
    data?.incidents?.edges?.map((f) => {
      const incident = f.node
      const feature: Feature = {
        type: 'Feature',
        id: incident.id,
        geometry: {
          type: 'Point',
          coordinates: [incident.longitude, incident.latitude],
        },
        properties: {
          id: incident.id,
          type: PointType.MarketIncident,
          name: incident.name,
          facilityId: incident.facility?.id,
          facilityName: incident.facility?.name,
          address: incident.address,
          displayId: incident.displayId,
          latitude: incident.latitude,
          longitude: incident.longitude,
        },
      }
      return feature
    }) || []
  )
}

export function dataToGeoJSONPoint(
  facilityData: GlobalMapProperties[],
  marketIncidentsData: IncidentsByMarketFacilityQuery
): Feature[] {
  const facilityPoints = facilityToGeoJSONPoint(facilityData)
  const marketIncidentPoints =
    marketIncidentsToGeoJSONPoint(marketIncidentsData)

  return [...facilityPoints, ...marketIncidentPoints]
}
